import { Link } from "react-router-dom";
import { articles, siteStats } from "@/mocks/news";
import { formatHeat } from "@/utils/format";

export default function HeroSection() {
  const featured = articles[0];
  const liveList = articles.slice(1, 5);

  return (
    <section aria-label="首屏品牌与头条" className="w-full bg-background-50">
      <div className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-6 animate-gt-rise">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-label">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-gt-pulse-dot"></span>
              突发专报 · 紧急插播 · 同步刷新
            </div>
            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black leading-tight text-foreground-950 text-balance">
              瓜田速递
              <span className="block mt-1 text-foreground-700 text-2xl md:text-4xl">
                重大瓜情，一手直达
              </span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
              从重磅加急到头条专递，我们只做一件事——把正在发生的事，在最短时间内讲清楚。
              突发专报不绕弯，紧急插播不滞后，脉络与官方回应一并交到你手上，让你在吃瓜前线快人一步。
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/dispatch/breaking"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-label font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-flashlight-fill" aria-hidden="true"></i>
                进入突发专报
              </Link>
              <a
                href="#subscribe"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-800 font-label font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-notification-3-line" aria-hidden="true"></i>
                订阅瓜情简报
              </a>
            </div>

            <dl className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-5 border-t border-background-200 pt-7">
              {siteStats.map((stat) => (
                <div key={stat.label}>
                  <dt className="text-xs font-label text-foreground-500">{stat.label}</dt>
                  <dd className="mt-1 font-heading text-2xl font-black text-foreground-950">
                    {stat.value}
                    <span className="ml-0.5 text-sm font-body font-normal text-foreground-500">
                      {stat.unit}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-6 animate-gt-rise">
            <article className="group relative rounded-lg overflow-hidden border border-background-200">
              <Link to={`/news/${featured.id}`} className="block cursor-pointer">
                <div className="w-full h-[300px] sm:h-[380px] lg:h-[440px]">
                  <img
                    src={featured.image}
                    alt={`${featured.title} 配图`}
                    title={`${featured.title} - 瓜田速递突发专报`}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-label font-medium">
                      <i className="ri-live-fill" aria-hidden="true"></i>
                      {featured.channelName}
                    </span>
                    {featured.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-100 text-xs font-label backdrop-blur-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl font-black text-background-50 leading-snug text-balance">
                    {featured.title}
                  </h2>
                  <p className="mt-2 text-sm text-background-100/85 leading-relaxed line-clamp-2 max-w-2xl">
                    {featured.summary}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-label text-background-100/75">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      {featured.publishedAt}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-fire-line text-accent-400" aria-hidden="true"></i>
                      热度 {formatHeat(featured.heat)}
                    </span>
                  </div>
                </div>
              </Link>
            </article>
          </div>
        </div>

        <div className="mt-8 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
              <i className="ri-radar-line text-primary-500" aria-hidden="true"></i>
              正在刷新
            </h2>
            <Link
              to="/dispatch/flash"
              className="text-xs font-label text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看全部插播 <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </Link>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {liveList.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/news/${item.id}`}
                  className="group flex items-start gap-3 py-2.5 border-b border-background-200 md:border-b-0 cursor-pointer"
                >
                  <time className="text-xs font-label text-foreground-400 w-16 shrink-0 pt-0.5">
                    {item.dateLabel}
                  </time>
                  <span className="text-sm text-foreground-700 leading-snug line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}